/* ============================================================================
   SCOREBORN FLIGHT — WING RIDER
   A fully articulated man in a navy suit standing on the SC-1.
   Systems: CharacterModel (articulated, hand-built — see PROVISIONAL note)
   · AttachmentController (aircraft-local anchor) · BalanceController
   (real flight telemetry) · Leg/Arm analytic IK · StateMachine (idle/balance/
   brace/sit/stand/reach/grip/flips/crash) · TieSim (verlet) · HairSway ·
   ActionPanel UI · LODManager · camera hints.

   PROVISIONAL ASSET NOTE — the mesh is built in code from smoothed lathe/
   capsule surfaces (no third-party assets, nothing to license). It is a
   deliberate stand-in at the architecture's lowest layer: CharacterModel
   returns a joint map {hips,spine,chest,neck,head,shoulder/elbow/wrist L+R,
   hip/knee/ankle L+R} — a licensed realistic GLB (Mixamo-class skeleton)
   can replace buildBody() by mapping bones to the same joint names; every
   other system (balance, IK, states, flips, tie, UI) operates on that joint
   interface and carries over unchanged.
   ============================================================================ */
(function () {
"use strict";

var CFG = {
  height: 1.80,
  deck: { y: 0.585, z: 0.85 },        /* stand point on fuselage top, aircraft-local */
  seatZ: 1.55,                         /* recessed seat centre */
  handleZ: 0.28,                       /* integrated grip, forward of stance */
  footSpread: 0.17,
  thigh: 0.43, shin: 0.44, footH: 0.075,
  upperArm: 0.30, foreArm: 0.28,
  poseRate: 9,                         /* joint damping toward targets */
  balanceRate: 5,
  lodNear: 22, lodFar: 55,
  flipDur: 1.25, flipHeight: 0.92, 
  gripBankOn: 1.75, gripBankOff: 1.05, /* rad, hysteresis */
  gripGOn: -0.15, gripGOff: 0.35,
  severeRate: 1.9                      /* combined rate that forces brace */
};

function clamp(v,a,b){return v<a?a:v>b?b:v}
function lerp(a,b,t){return a+(b-a)*t}
function dampf(c,t,r,dt){return lerp(c,t,1-Math.exp(-r*dt))}
function easeInOut(t){return t*t*(3-2*t)}

window.StanhopeCharacter = { create: create };

function create(api) {
  var THREE = api.THREE;
  var V3 = function(x,y,z){return new THREE.Vector3(x,y,z)};

  /* ---------------- materials (PBR, restrained) ---------------- */
  var M = {
    suit:  new THREE.MeshStandardMaterial({ color: 0x1d2b47, roughness: 0.82, metalness: 0.02, envMapIntensity: 0.55 }),
    suitDark: new THREE.MeshStandardMaterial({ color: 0x16223a, roughness: 0.85, metalness: 0.02, envMapIntensity: 0.5 }),
    shirt: new THREE.MeshStandardMaterial({ color: 0xe9e8e0, roughness: 0.65, metalness: 0, envMapIntensity: 0.35 }),
    tie:   new THREE.MeshStandardMaterial({ color: 0x8e1220, roughness: 0.42, metalness: 0.05, envMapIntensity: 0.8, side: THREE.DoubleSide }),
    skin:  new THREE.MeshStandardMaterial({ color: 0xd8a184, roughness: 0.58, metalness: 0, envMapIntensity: 0.4 }),
    skinDark: new THREE.MeshStandardMaterial({ color: 0xc78f74, roughness: 0.6, metalness: 0 }),
    hair:  new THREE.MeshStandardMaterial({ color: 0xb8914d, roughness: 0.62, metalness: 0.05, envMapIntensity: 0.7 }),
    hairLight: new THREE.MeshStandardMaterial({ color: 0xd4b06a, roughness: 0.55, metalness: 0.05, envMapIntensity: 0.8 }),
    brow:  new THREE.MeshStandardMaterial({ color: 0x8a6a3a, roughness: 0.8 }),
    eye:   new THREE.MeshStandardMaterial({ color: 0xf2efe8, roughness: 0.25, envMapIntensity: 1.1 }),
    iris:  new THREE.MeshStandardMaterial({ color: 0x4d6d80, roughness: 0.2, envMapIntensity: 1.2 }),
    lip:   new THREE.MeshStandardMaterial({ color: 0xb97e6d, roughness: 0.55 }),
    shoe:  new THREE.MeshStandardMaterial({ color: 0x15161a, roughness: 0.28, metalness: 0.12, envMapIntensity: 1.2 }),
    metal: new THREE.MeshStandardMaterial({ color: 0x8d9298, roughness: 0.35, metalness: 0.8 }),
    gripRubber: new THREE.MeshStandardMaterial({ color: 0x23262b, roughness: 0.9, metalness: 0.05 }),
    seat:  new THREE.MeshStandardMaterial({ color: 0x252c3a, roughness: 0.88, metalness: 0.03 })
  };
  var disposables = [];
  function G(g){disposables.push(g);return g}
  function mesh(g,m){var o=new THREE.Mesh(G(g),m);o.castShadow=true;return o}
  /* smoothed limb capsule */
  function capsule(r1,r2,len,seg){ /* along -Y from origin */
    var pts=[],i,steps=7;
    for(i=0;i<=3;i++){var a=Math.PI/2*(i/3);pts.push(new THREE.Vector2(Math.cos(a-Math.PI/2)*r1*0.99+0.001? Math.cos(a)*0+0.001:0.001,0));}
    pts=[];
    pts.push(new THREE.Vector2(0.001,0));
    for(i=0;i<=3;i++){var t=i/3;pts.push(new THREE.Vector2(lerp(r1*0.55,r1,t),-r1*0.28*(1-t)));}
    pts.push(new THREE.Vector2(r1, -r1*0.28));
    pts.push(new THREE.Vector2(lerp(r1,r2,0.5), -len*0.5));
    pts.push(new THREE.Vector2(r2, -len+r2*0.3));
    pts.push(new THREE.Vector2(r2*0.6, -len));
    pts.push(new THREE.Vector2(0.001, -len));
    var g=new THREE.LatheGeometry(pts, seg||12);
    return g;
  }

  /* ---------------- aircraft fixtures: handle + seat ---------------- */
  var fixtures = new THREE.Group();
  (function buildFixtures(){
    /* recessed maintenance grip: shallow dark inset + low bar with end mounts */
    var inset = mesh(new THREE.BoxGeometry(0.34,0.035,0.14), M.gripRubber);
    inset.position.set(0, CFG.deck.y-0.012, CFG.handleZ);
    fixtures.add(inset);
    var bar = mesh(new THREE.CylinderGeometry(0.021,0.021,0.26,10), M.metal);
    bar.rotation.z = Math.PI/2;
    bar.position.set(0, CFG.deck.y+0.052, CFG.handleZ);
    fixtures.add(bar);
    [-1,1].forEach(function(s){
      var mount = mesh(new THREE.BoxGeometry(0.035,0.075,0.06), M.metal);
      mount.position.set(0.13*s, CFG.deck.y+0.02, CFG.handleZ);
      fixtures.add(mount);
    });
    /* recessed seat pad aft of stance */
    var well = mesh(new THREE.BoxGeometry(0.46,0.05,0.42), M.gripRubber);
    well.position.set(0, CFG.deck.y-0.045, CFG.seatZ);
    fixtures.add(well);
    var pad = mesh(new THREE.BoxGeometry(0.4,0.045,0.36), M.seat);
    pad.position.set(0, CFG.deck.y-0.02, CFG.seatZ);
    fixtures.add(pad);
    var back = mesh(new THREE.BoxGeometry(0.4,0.16,0.05), M.seat);
    back.position.set(0, CFG.deck.y+0.05, CFG.seatZ+0.19);
    back.rotation.x = -0.28;
    fixtures.add(back);
  })();
  api.aircraftModel.add(fixtures);

  /* ---------------- articulated body ---------------- */
  var anchor = new THREE.Group();               /* aircraft-local character space */
  anchor.position.set(0, 0, CFG.deck.z);
  api.aircraftModel.add(anchor);
  var J = {};                                    /* joints */
  function joint(name,parent,x,y,z){var g=new THREE.Group();g.position.set(x,y,z);parent.add(g);J[name]=g;return g}

  var meshRefs = { small: [], face: [] };
  (function buildBody(){
    var hipY = CFG.deck.y + CFG.thigh + CFG.shin + CFG.footH; /* standing hip height */
    var hips = joint("hips", anchor, 0, hipY, 0);
    var spine = joint("spine", hips, 0, 0.10, 0);
    var chest = joint("chest", spine, 0, 0.22, 0);
    var neck  = joint("neck",  chest, 0, 0.24, 0.01);
    var head  = joint("head",  neck, 0, 0.055, 0);

    /* pelvis + jacket skirt */
    var pelvis = mesh(new THREE.SphereGeometry(0.155,14,10), M.suit);
    pelvis.scale.set(1.15,0.78,0.82); pelvis.position.y = 0.02; hips.add(pelvis);
    var skirt = mesh(new THREE.CylinderGeometry(0.19,0.215,0.2,14,1,true), M.suit);
    skirt.position.y = -0.07; hips.add(skirt);

    /* torso: shirt core + jacket shell + lapels */
    var shirtCore = mesh(new THREE.CylinderGeometry(0.128,0.112,0.30,14), M.shirt);
    shirtCore.position.y = 0.15; shirtCore.scale.z = 0.72; spine.add(shirtCore);
    var jacket = mesh(capsule(0.19,0.165,0.42,16), M.suit);
    jacket.position.y = 0.36; jacket.scale.set(1.18,1,0.88); spine.add(jacket);
    /* open jacket front: shirt V wedge */
    var vShirt = mesh(new THREE.CylinderGeometry(0.075,0.05,0.3,3,1), M.shirt);
    vShirt.rotation.y = Math.PI; vShirt.position.set(0,0.22,-0.128); vShirt.scale.z=0.42; spine.add(vShirt);
    [-1,1].forEach(function(s){
      var lapel = mesh(new THREE.BoxGeometry(0.055,0.24,0.02), M.suitDark);
      lapel.position.set(0.055*s,0.24,-0.155);
      lapel.rotation.set(0.08, -0.5*s, 0.16*s);
      spine.add(lapel);
    });
    /* collar */
    var collar = mesh(new THREE.TorusGeometry(0.07,0.024,8,14,Math.PI*1.5), M.shirt);
    collar.rotation.set(Math.PI/2,0,Math.PI*0.75);
    collar.position.set(0,0.445,0.01); spine.add(collar);
    /* shoulders (jacket) */
    [-1,1].forEach(function(s){
      var pad = mesh(new THREE.SphereGeometry(0.085,12,9), M.suit);
      pad.position.set(0.205*s,0.44,0); pad.scale.set(1.15,0.85,1); spine.add(pad);
    });

    /* head + face */
    var skull = mesh(new THREE.SphereGeometry(0.108,18,14), M.skin);
    skull.scale.set(0.88,1.08,0.94); skull.position.y=0.075; head.add(skull);
    var jaw = mesh(new THREE.SphereGeometry(0.075,14,10), M.skin);
    jaw.scale.set(0.82,0.72,0.8); jaw.position.set(0,-0.012,-0.012); head.add(jaw);
    var chin = mesh(new THREE.SphereGeometry(0.032,10,8), M.skin);
    chin.position.set(0,-0.045,-0.062); head.add(chin);
    var noseG = mesh(new THREE.ConeGeometry(0.016,0.05,6), M.skinDark);
    noseG.rotation.x=-Math.PI/2-0.35; noseG.position.set(0,0.035,-0.098); head.add(noseG);
    meshRefs.face.push(noseG);
    [-1,1].forEach(function(s){
      var ear = mesh(new THREE.SphereGeometry(0.024,8,6), M.skinDark);
      ear.scale.set(0.5,1,0.7); ear.position.set(0.088*s,0.045,0.012); head.add(ear);
      var eye = mesh(new THREE.SphereGeometry(0.0155,10,8), M.eye);
      eye.position.set(0.034*s,0.052,-0.082); head.add(eye);
      var iris = mesh(new THREE.SphereGeometry(0.008,8,6), M.iris);
      iris.position.set(0.034*s,0.052,-0.0955); head.add(iris);
      meshRefs.face.push(eye,iris);
      var lid = mesh(new THREE.SphereGeometry(0.0165,10,6,0,Math.PI*2,0,Math.PI*0.55), M.skin);
      lid.position.set(0.034*s,0.0535,-0.0815);
      lid.rotation.x = -0.5; lid.scale.y = 0.30;
      head.add(lid); meshRefs.face.push(lid);
      if(s<0) meshRefs.lidL = lid; else meshRefs.lidR = lid;
      var brow = mesh(new THREE.BoxGeometry(0.042,0.008,0.012), M.brow);
      brow.position.set(0.035*s,0.078,-0.088); brow.rotation.z = -0.12*s; head.add(brow);
      meshRefs.face.push(brow);
    });
    var lips = mesh(new THREE.BoxGeometry(0.045,0.011,0.014), M.lip);
    lips.position.set(0,-0.021,-0.086); head.add(lips); meshRefs.face.push(lips);
    var neckM = mesh(new THREE.CylinderGeometry(0.052,0.06,0.11,10), M.skin);
    neckM.position.y=-0.015; neck.add(neckM);

    /* wavy blond hair — scalp cap + layered locks */
    var hairG = new THREE.Group(); head.add(hairG); meshRefs.hair = hairG;
    var cap = mesh(new THREE.SphereGeometry(0.106,16,12,0,Math.PI*2,0,Math.PI*0.55), M.hair);
    cap.scale.set(0.9,1.02,0.97); cap.position.y=0.082; hairG.add(cap);
    var i, lockSeed = 7;
    for(i=0;i<16;i++){
      var a = (i/16)*Math.PI*2;
      var rr = 0.089 + ((i*37)%10)*0.0012;
      var lock = mesh(new THREE.SphereGeometry(0.030,7,5), (i%3===0)?M.hairLight:M.hair);
      lock.scale.set(0.75,0.5+((i*13)%7)*0.05,1.25);
      lock.position.set(Math.cos(a)*rr*0.88, 0.115+Math.sin(a*3+lockSeed)*0.02, Math.sin(a)*rr*0.9-0.01);
      lock.rotation.set(Math.sin(a*2)*0.5, a+Math.PI/2, Math.cos(a)*0.35);
      hairG.add(lock);
    }
    /* front sweep */
    for(i=0;i<5;i++){
      var f = mesh(new THREE.SphereGeometry(0.030,7,5), i%2?M.hairLight:M.hair);
      f.scale.set(0.85,0.45,1.15);
      f.position.set(-0.052+i*0.026, 0.142-Math.abs(i-2)*0.008, -0.075);
      f.rotation.set(-0.55, (i-2)*0.28, (i-2)*0.18);
      hairG.add(f);
    }
    /* sideburns / nape */
    [-1,1].forEach(function(s){
      var sb = mesh(new THREE.BoxGeometry(0.02,0.05,0.03), M.hair);
      sb.position.set(0.085*s,0.02,0.005); hairG.add(sb);
    });

    /* arms */
    [-1,1].forEach(function(s){
      var suff = s<0?"L":"R";
      var sh = joint("shoulder"+suff, chest, 0.215*s, 0.20, 0);
      var el = joint("elbow"+suff, sh, 0, -CFG.upperArm, 0);
      var wr = joint("wrist"+suff, el, 0, -CFG.foreArm, 0);
      var ua = mesh(capsule(0.062,0.052,CFG.upperArm,10), M.suit); sh.add(ua);
      var fa = mesh(capsule(0.051,0.041,CFG.foreArm,10), M.suit); el.add(fa);
      var cuff = mesh(new THREE.CylinderGeometry(0.041,0.041,0.035,10), M.shirt);
      cuff.position.y = -CFG.foreArm+0.05; el.add(cuff);
      /* hand: palm + finger block + thumb (grip-capable) */
      var hand = new THREE.Group(); wr.add(hand); J["hand"+suff]=hand;
      var palm = mesh(new THREE.BoxGeometry(0.075,0.085,0.03), M.skin);
      palm.position.y=-0.045; hand.add(palm);
      var fingers = new THREE.Group(); fingers.position.set(0,-0.088,0); hand.add(fingers);
      J["fingers"+suff]=fingers;
      var fb = mesh(new THREE.BoxGeometry(0.07,0.062,0.026), M.skin);
      fb.position.y=-0.03; fingers.add(fb);
      var fb2 = mesh(new THREE.BoxGeometry(0.066,0.045,0.024), M.skin);
      fb2.position.set(0,-0.068,-0.004); fingers.add(fb2);
      meshRefs.small.push(fb2);
      var thumb = mesh(new THREE.BoxGeometry(0.024,0.05,0.024), M.skin);
      thumb.position.set(0.045*s,-0.035,-0.005); thumb.rotation.z=0.5*s; hand.add(thumb);
      J["thumb"+suff]=thumb;
    });

    /* legs */
    [-1,1].forEach(function(s){
      var suff = s<0?"L":"R";
      var hip = joint("hip"+suff, hips, CFG.footSpread*0.85*s, -0.06, 0);
      var knee = joint("knee"+suff, hip, 0, -CFG.thigh, 0);
      var ankle = joint("ankle"+suff, knee, 0, -CFG.shin, 0);
      var th = mesh(capsule(0.095,0.07,CFG.thigh,12), M.suitDark); hip.add(th);
      var sh2 = mesh(capsule(0.068,0.05,CFG.shin,10), M.suitDark); knee.add(sh2);
      /* polished dress shoe */
      var shoe = new THREE.Group(); ankle.add(shoe); J["shoe"+suff]=shoe;
      var heel = mesh(new THREE.BoxGeometry(0.085,0.075,0.11), M.shoe);
      heel.position.set(0,-0.038,0.015); shoe.add(heel);
      var toe = mesh(new THREE.SphereGeometry(0.048,10,8), M.shoe);
      toe.scale.set(0.85,0.62,1.7); toe.position.set(0,-0.052,-0.095); shoe.add(toe);
    });
  })();

  /* red tie — verlet chain rendered as ribbon */
  var tie = (function(){
    var N=6, nodes=[], prev=[], segLen=0.075;
    for(var i=0;i<N;i++){nodes.push(V3(0,-i*segLen,0));prev.push(V3(0,-i*segLen,0));}
    var geo = G(new THREE.BufferGeometry());
    var pos = new Float32Array(N*2*3);
    geo.setAttribute("position", new THREE.BufferAttribute(pos,3));
    var idx=[];
    for(var q2=0;q2<N-1;q2++){var a2=q2*2;idx.push(a2,a2+1,a2+2, a2+1,a2+3,a2+2);}
    geo.setIndex(idx);
    var nrmA = new Float32Array(N*2*3);
    for (var n2=0;n2<N*2;n2++){ nrmA[n2*3+2] = -1; }
    geo.setAttribute("normal", new THREE.BufferAttribute(nrmA,3));
    var m = new THREE.Mesh(geo, M.tie);
    m.castShadow = true; m.frustumCulled = false;
    J.spine.add(m);
    /* knot */
    var knot = mesh(new THREE.SphereGeometry(0.026,8,6), M.tie);
    knot.scale.set(1,1.2,0.7); knot.position.set(0,0.415,-0.135); J.spine.add(knot);
    return { nodes:nodes, prev:prev, segLen:segLen, geo:geo, posAttr:geo.attributes.position, root:V3(0,0.39,-0.14), mesh:m };
  })();

  /* ---------------- pose library (joint euler targets) ---------------- */
  var POSES = {
    idle: {
      spine:[0.04,0,0], chest:[0.02,0,0], neck:[-0.05,0,0], head:[0.02,0,0],
      shoulderL:[0.06,0,0.10], shoulderR:[0.06,0,-0.10],
      elbowL:[-0.22,0,0.05], elbowR:[-0.22,0,-0.05],
      wristL:[0,0,0], wristR:[0,0,0], fingersL:[-0.25,0,0], fingersR:[-0.25,0,0]
    },
    brace: {
      spine:[0.30,0,0], chest:[0.12,0,0], neck:[-0.28,0,0], head:[-0.05,0,0],
      shoulderL:[0.5,0,0.85], shoulderR:[0.5,0,-0.85],
      elbowL:[-0.5,0,0.2], elbowR:[-0.5,0,-0.2],
      fingersL:[-0.5,0,0], fingersR:[-0.5,0,0]
    },
    sit: {
      spine:[0.10,0,0], chest:[0.03,0,0], neck:[-0.10,0,0], head:[0,0,0],
      shoulderL:[0.55,0,0.12], shoulderR:[0.55,0,-0.12],
      elbowL:[-0.85,0,0], elbowR:[-0.85,0,0],
      wristL:[0.3,0,0], wristR:[0.3,0,0]
    },
    grip: {
      spine:[0.42,0,-0.1], chest:[0.18,0,0], neck:[-0.4,0,0.08], head:[-0.08,0,0],
      shoulderL:[0.2,0,1.1], elbowL:[-0.6,0,0.3],
      fingersL:[-0.6,0,0]
    },
    tuck: {
      spine:[0.55,0,0], chest:[0.35,0,0], neck:[0.3,0,0], head:[0.15,0,0],
      shoulderL:[1.5,0,0.35], shoulderR:[1.5,0,-0.35],
      elbowL:[-1.9,0,0], elbowR:[-1.9,0,0],
      fingersL:[-0.9,0,0], fingersR:[-0.9,0,0]
    },
    land: {
      spine:[0.34,0,0], chest:[0.14,0,0], neck:[-0.3,0,0],
      shoulderL:[0.35,0,0.9], shoulderR:[0.35,0,-0.9],
      elbowL:[-0.4,0,0.1], elbowR:[-0.4,0,-0.1]
    }
  };

  /* ---------------- runtime state ---------------- */
  var st = { name:"standingIdle", t:0, dur:0, flipDir:0 };
  var tele = { rollRate:0,pitchRate:0,yawRate:0,g:1,speed:0,upY:1,bank:0,latA:0,lonA:0,onGround:false,crashed:false };
  var bal = { lean:0, pitchLean:0, crouch:0, armRaise:0, shift:0, sway:0, severity:0 };
  var gripT = 0, releaseT = 0, gripping = false;
  var rootOff = { x:0,y:0,z:0, rx:0 };            /* dynamic root offsets (flips, crouch) */
  var footTargets = { L:V3(-CFG.footSpread,CFG.deck.y+CFG.footH,0.02), R:V3(CFG.footSpread,CFG.deck.y+CFG.footH,-0.02) };
  var footLock = 1;                                /* 1 planted, 0 free */
  var blinkT = 2.5, blinkPhase = 0;
  var lod = 0;                                     /* 0 near 1 mid 2 far */
  var noiseT = Math.random()*100;
  var standHipY = CFG.deck.y + CFG.footH + CFG.thigh + CFG.shin - 0.02;

  /* ---------------- helpers ---------------- */
  var _q = new THREE.Quaternion(), _v = V3(), _v2 = V3(), _v3 = V3(), _m = new THREE.Matrix4();
  function poseBlend(a, b, tt, joints) {
    /* write blended euler targets into targets map */
    var out = {};
    var keys = {};
    var k;
    for (k in a) keys[k]=1; for (k in b) keys[k]=1;
    for (k in keys) {
      var pa = a[k] || [0,0,0], pb = b[k] || [0,0,0];
      out[k] = [lerp(pa[0],pb[0],tt), lerp(pa[1],pb[1],tt), lerp(pa[2],pb[2],tt)];
    }
    return out;
  }
  function applyTargets(tg, rate, dt, add) {
    for (var k in tg) {
      var j = J[k]; if (!j) continue;
      var t3 = tg[k];
      var ax = t3[0]+(add&&add[k]?add[k][0]:0), ay=t3[1]+(add&&add[k]?add[k][1]:0), az=t3[2]+(add&&add[k]?add[k][2]:0);
      j.rotation.x = dampf(j.rotation.x, ax, rate, dt);
      j.rotation.y = dampf(j.rotation.y, ay, rate, dt);
      j.rotation.z = dampf(j.rotation.z, az, rate, dt);
    }
  }
  /* analytic 2-bone leg IK in anchor space */
  function solveLeg(suff, target, dt, weight) {
    var hip = J["hip"+suff], knee = J["knee"+suff], ankle = J["ankle"+suff];
    hip.getWorldPosition(_v); anchor.worldToLocal(_v);         /* hip pos, anchor space */
    _v2.copy(target).sub(_v);
    var d = clamp(_v2.length(), 0.25, CFG.thigh+CFG.shin-0.015);
    /* knee angle by law of cosines */
    var a2=CFG.thigh*CFG.thigh, b2=CFG.shin*CFG.shin;
    var kneeA = Math.acos(clamp((a2+b2-d*d)/(2*CFG.thigh*CFG.shin), -1, 1));
    var hipA  = Math.acos(clamp((a2+d*d-b2)/(2*CFG.thigh*d), -1, 1));
    /* direction hip->target in hips-local frame */
    _v3.copy(_v2).normalize();
    /* convert dir to hips parent frame: hips rotation affects children; approximate by removing root rx */
    var pitchDir = Math.atan2(-_v3.z, -_v3.y);   /* forward lean of leg line */
    var sideDir  = Math.atan2(_v3.x*(suff==="L"?1:1), -_v3.y) * 0.9;
    var thx = pitchDir + hipA;                    /* knee forward of the leg line */
    var kx  = -(Math.PI - kneeA);
    var rate = 14;
    hip.rotation.x = dampf(hip.rotation.x, lerp(hip.rotation.x, thx - J.hips.rotation.x*0.9, weight), rate, dt);
    hip.rotation.z = dampf(hip.rotation.z, sideDir*weight, rate, dt);
    knee.rotation.x = dampf(knee.rotation.x, lerp(knee.rotation.x, -kx*0 + kx, weight), rate, dt);
    /* foot flat on deck */
    ankle.rotation.x = dampf(ankle.rotation.x, (-hip.rotation.x - knee.rotation.x - J.hips.rotation.x)*weight, rate, dt);
    ankle.rotation.z = dampf(ankle.rotation.z, -hip.rotation.z*weight, rate, dt);
  }
  /* 2-bone arm IK: aim right hand at a target in anchor space */
  function solveArm(suff, target, dt, weight) {
    var sh = J["shoulder"+suff], el = J["elbow"+suff];
    sh.getWorldPosition(_v); anchor.worldToLocal(_v);
    _v2.copy(target).sub(_v);
    var d = clamp(_v2.length(), 0.12, CFG.upperArm+CFG.foreArm-0.01);
    var a2=CFG.upperArm*CFG.upperArm, b2=CFG.foreArm*CFG.foreArm;
    var elA = Math.acos(clamp((a2+b2-d*d)/(2*CFG.upperArm*CFG.foreArm), -1, 1));
    var shA = Math.acos(clamp((a2+d*d-b2)/(2*CFG.upperArm*d), -1, 1));
    _v3.copy(_v2).normalize();
    var pitch = Math.atan2(-_v3.z, -_v3.y);
    var side  = Math.atan2(_v3.x, -_v3.y);
    var rate = 12;
    sh.rotation.x = dampf(sh.rotation.x, (pitch + shA*0.9)*weight + POSES.grip.spine[0]*0, rate, dt);
    sh.rotation.z = dampf(sh.rotation.z, side*weight, rate, dt);
    sh.rotation.y = dampf(sh.rotation.y, 0, rate, dt);
    el.rotation.x = dampf(el.rotation.x, -(Math.PI-elA)*weight, rate, dt);
    el.rotation.z = dampf(el.rotation.z, 0, rate, dt);
    /* wrist wraps the bar (bar runs along X) */
    var wr = J["wrist"+suff];
    wr.rotation.x = dampf(wr.rotation.x, -0.5*weight, rate, dt);
    J["fingers"+suff].rotation.x = dampf(J["fingers"+suff].rotation.x, -1.35*weight-0.25, rate, dt);
    J["thumb"+suff].rotation.x = dampf(J["thumb"+suff].rotation.x, 0.8*weight, rate, dt);
  }

  /* ---------------- state machine ---------------- */
  function setState(name, dur, flipDir) {
    st.name = name; st.t = 0; st.dur = dur || 0; st.flipDir = flipDir || 0;
    panelDirty = true;
  }
  var TRANSIENT = { standingUp:0.9, sittingDown:0.95, reachingForHandle:0.5, releasingHandle:0.6,
                    frontFlip:CFG.flipDur, backflip:CFG.flipDur, recovery:0.55, crashReaction:0 };
  function isFlipping(){ return st.name==="frontFlip" || st.name==="backflip"; }
  function isBusy(){ return st.name in TRANSIENT && st.name!=="crashReaction"; }
  function canFlip(){
    return !tele.crashed && !gripping && !isBusy() &&
      (st.name==="standingIdle"||st.name==="balancing"||st.name==="bracing") &&
      bal.severity < 1.15 && tele.upY > 0.2;
  }
  function canStand(){ return !tele.crashed && !gripping && (st.name==="seatedIdle") ; }
  function canSit(){ return !tele.crashed && !gripping && (st.name==="standingIdle"||st.name==="balancing") && bal.severity < 1.0; }

  function action(name) {
    if (api.isPaused && api.isPaused()) return false;
    if (name==="stand" && canStand()) { setState("standingUp", TRANSIENT.standingUp); return true; }
    if (name==="sit" && canSit()) { setState("sittingDown", TRANSIENT.sittingDown); return true; }
    if (name==="frontflip" && canFlip()) { setState("frontFlip", CFG.flipDur, -1); return true; }
    if (name==="backflip" && canFlip()) { setState("backflip", CFG.flipDur, 1); return true; }
    return false;
  }

  /* ---------------- per-frame update ---------------- */
  function update(dt, camera, now) {
    dt = Math.min(dt, 0.05);
    noiseT += dt;
    var T = api.getTelemetry();
    /* smooth telemetry */
    tele.rollRate = dampf(tele.rollRate, T.rollRate, 8, dt);
    tele.pitchRate = dampf(tele.pitchRate, T.pitchRate, 8, dt);
    tele.yawRate = dampf(tele.yawRate, T.yawRate, 8, dt);
    tele.g = dampf(tele.g, T.g, 6, dt);
    tele.speed = T.speed; tele.upY = T.upY; tele.bank = T.bank;
    tele.onGround = T.onGround; tele.crashed = T.crashed;

    /* LOD from camera distance */
    anchor.getWorldPosition(_v);
    var camDist = camera.position.distanceTo(_v);
    lod = camDist < CFG.lodNear ? 0 : camDist < CFG.lodFar ? 1 : 2;
    var visible = camDist < 220;
    anchor.visible = visible; fixtures.visible = true;

    /* balance signals */
    bal.severity = dampf(bal.severity,
      Math.abs(tele.rollRate)+Math.abs(tele.pitchRate)*1.2+Math.abs(tele.yawRate)+Math.abs(tele.g-1)*0.9, 4, dt);
    var wind = clamp(tele.speed/120, 0, 1.4);
    bal.lean   = dampf(bal.lean,  clamp(-tele.rollRate*0.55 - tele.yawRate*0.2, -0.5, 0.5), CFG.balanceRate, dt);
    bal.pitchLean = dampf(bal.pitchLean, clamp(tele.pitchRate*0.5 + (tele.g-1)*0.12 + wind*0.10, -0.45, 0.5), CFG.balanceRate, dt);
    bal.crouch = dampf(bal.crouch, clamp((tele.g-1)*0.16 + bal.severity*0.14 + wind*0.06, 0, 0.34), 4, dt);
    bal.armRaise = dampf(bal.armRaise, clamp(bal.severity*0.55, 0, 1), 5, dt);
    bal.shift = dampf(bal.shift, clamp(tele.rollRate*0.09, -0.09, 0.09), 4, dt);
    /* subtle life: weight sway + micro corrections */
    bal.sway = Math.sin(noiseT*0.6)*0.012 + Math.sin(noiseT*1.7+2)*0.007 + (Math.sin(noiseT*5.3)*0.004)*bal.severity;

    /* grip decision (hysteresis over bank / negative G / severity) */
    var wantGrip = (Math.abs(tele.bank) > CFG.gripBankOn) || (tele.g < CFG.gripGOn) ||
                   (bal.severity > 2.5) || tele.upY < 0;
    var wantRelease = (Math.abs(tele.bank) < CFG.gripBankOff) && (tele.g > CFG.gripGOff) &&
                   bal.severity < 1.1 && tele.upY > 0.55;
    if (!gripping && wantGrip && !isFlipping() && !tele.crashed) {
      gripT += dt;
      if (gripT > 0.12) { gripping = true; releaseT = 0; if (st.name!=="handleGrip") setState("reachingForHandle", TRANSIENT.reachingForHandle); }
    } else gripT = 0;
    if (gripping && wantRelease) {
      releaseT += dt;
      if (releaseT > 0.45) { gripping = false; setState("releasingHandle", TRANSIENT.releasingHandle); }
    } else releaseT = 0;

    if (tele.crashed && st.name!=="crashReaction") setState("crashReaction");

    /* state timeline */
    st.t += dt;
    var n = st.name;
    if (n==="reachingForHandle" && st.t>=st.dur) setState("handleGrip");
    else if (n==="releasingHandle" && st.t>=st.dur) setState("standingIdle");
    else if (n==="standingUp" && st.t>=st.dur) setState("standingIdle");
    else if (n==="sittingDown" && st.t>=st.dur) setState("seatedIdle");
    else if ((n==="frontFlip"||n==="backflip") && st.t>=st.dur) setState("recovery", TRANSIENT.recovery);
    else if (n==="recovery" && st.t>=st.dur) setState("standingIdle");
    else if (n==="crashReaction" && !tele.crashed) setState("standingIdle");
    else if (n==="standingIdle" && bal.severity>0.55) setState("balancing");
    else if (n==="balancing" && bal.severity<=0.4) setState("standingIdle");
    else if ((n==="standingIdle"||n==="balancing") && bal.severity>CFG.severeRate) setState("bracing");
    else if (n==="bracing" && bal.severity<CFG.severeRate*0.72) setState("balancing");
    n = st.name;                 /* re-read after transitions: st.t/st.dur must match */

    if (visible) {
    /* ---------- root placement + pose per state ---------- */
    var base = POSES.idle, footW = 1, ikArm = 0;
    var targetRoot = { x: bal.shift + bal.sway, y: standHipY - bal.crouch, z: 0, rx: 0, rz: 0 };

    if (n==="seatedIdle" || n==="sittingDown" || (n==="standingUp")) {
      var sitAmt = n==="seatedIdle" ? 1 : n==="sittingDown" ? easeInOut(clamp(st.t/st.dur,0,1)) : 1-easeInOut(clamp(st.t/st.dur,0,1));
      base = poseBlend(POSES.idle, POSES.sit, sitAmt);
      targetRoot.z = lerp(0, CFG.seatZ-CFG.deck.z, sitAmt);
      targetRoot.y = lerp(standHipY - bal.crouch, CFG.deck.y+0.13, sitAmt);
      targetRoot.rx = lerp(0, -0.08, sitAmt);
      footTargets.L.set(-CFG.footSpread, CFG.deck.y+CFG.footH, lerp(0.02, CFG.seatZ-CFG.deck.z-0.48, sitAmt));
      footTargets.R.set(CFG.footSpread, CFG.deck.y+CFG.footH, lerp(-0.02, CFG.seatZ-CFG.deck.z-0.44, sitAmt));
    } else if (n==="reachingForHandle" || n==="handleGrip" || n==="releasingHandle") {
      var gAmt = n==="handleGrip" ? 1 : n==="reachingForHandle" ? easeInOut(clamp(st.t/st.dur,0,1)) : 1-easeInOut(clamp(st.t/st.dur,0,1));
      base = poseBlend(POSES.brace, POSES.grip, gAmt);
      ikArm = gAmt;
      targetRoot.y = standHipY - lerp(bal.crouch, 0.42, gAmt);
      targetRoot.z = lerp(0, -0.10, gAmt);
      targetRoot.rx = lerp(0, 0.15, gAmt);
      footTargets.L.set(-CFG.footSpread-0.03, CFG.deck.y+CFG.footH, 0.06);
      footTargets.R.set(CFG.footSpread+0.03, CFG.deck.y+CFG.footH, -0.10);
    } else if (n==="frontFlip" || n==="backflip") {
      var ft = clamp(st.t/st.dur, 0, 1);
      var dir = st.flipDir;                               /* -1 front, +1 back */
      var PREP=0.16, AIR0=PREP, AIR1=0.84;
      if (ft < PREP) {                                    /* preparation: crouch + arm swing */
        var p = ft/PREP;
        base = poseBlend(POSES.idle, POSES.land, easeInOut(p));
        targetRoot.y = standHipY - 0.30*easeInOut(p);
        footW = 1;
      } else if (ft < AIR1) {                             /* airborne: arc + rotation + tuck */
        var a = (ft-AIR0)/(AIR1-AIR0);
        var tuckAmt = Math.sin(Math.PI*clamp(a*1.15,0,1));
        base = poseBlend(POSES.land, POSES.tuck, clamp(tuckAmt*1.5,0,1));
        targetRoot.y = standHipY - 0.30 + Math.sin(Math.PI*a)*CFG.flipHeight + 0.15;
        targetRoot.z = dir * Math.sin(Math.PI*a) * 0.30;
        targetRoot.rx = dir * Math.PI*2 * (a<0.5 ? 2*a*a : 1-2*(1-a)*(1-a)); /* eased full rotation */
        footW = 0;
      } else {                                            /* landing compression */
        var l = (ft-AIR1)/(1-AIR1);
        base = poseBlend(POSES.land, POSES.idle, easeInOut(l)*0.35);
        targetRoot.y = standHipY - 0.34*(1-easeInOut(l)*0.4);
        targetRoot.rx = dir*Math.PI*2;
        footW = easeInOut(l);
      }
    } else if (n==="recovery") {
      var r = easeInOut(clamp(st.t/st.dur,0,1));
      base = poseBlend(POSES.land, POSES.idle, r);
      targetRoot.y = standHipY - lerp(0.2, bal.crouch, r);
      footTargets.L.set(-CFG.footSpread, CFG.deck.y+CFG.footH, 0.02);
      footTargets.R.set(CFG.footSpread, CFG.deck.y+CFG.footH, -0.02);
    } else if (n==="crashReaction") {
      base = POSES.brace;
      targetRoot.y = standHipY - 0.38;
      targetRoot.rx = 0.2;
    } else if (n==="bracing") {
      base = POSES.brace;
      targetRoot.y = standHipY - clamp(0.16+bal.crouch, 0, 0.4);
    } else {
      /* standingIdle / balancing: idle blended toward brace by severity + arm raise */
      base = poseBlend(POSES.idle, POSES.brace, clamp(bal.armRaise*0.8,0,1));
      footTargets.L.set(-CFG.footSpread + bal.shift*0.4, CFG.deck.y+CFG.footH, 0.02);
      footTargets.R.set(CFG.footSpread + bal.shift*0.4, CFG.deck.y+CFG.footH, -0.02);
    }

    /* balance additive layer (lean into the maneuver, not statue-tilt) */
    var add = {
      spine: [bal.pitchLean*0.55, 0, bal.lean*0.5],
      chest: [bal.pitchLean*0.3, -bal.lean*0.25, bal.lean*0.25],
      neck:  [-bal.pitchLean*0.35, bal.lean*0.3, -bal.lean*0.3],
      shoulderL: [0,0, bal.armRaise*0.35 + Math.max(0,-bal.lean)*0.4],
      shoulderR: [0,0,-bal.armRaise*0.35 - Math.max(0, bal.lean)*0.4]
    };

    /* flips rotate whole body but must never snap: damp fast during flip */
    var rootRate = isFlipping() ? 20 : 8;
    J.hips.position.x = dampf(J.hips.position.x, targetRoot.x, rootRate, dt);
    J.hips.position.y = dampf(J.hips.position.y, targetRoot.y, rootRate, dt);
    J.hips.position.z = dampf(J.hips.position.z, targetRoot.z, rootRate, dt);
    J.hips.rotation.x = dampf(J.hips.rotation.x, targetRoot.rx, isFlipping()?26:8, dt);
    J.hips.rotation.z = dampf(J.hips.rotation.z, (targetRoot.rz||0) + bal.lean*0.35, 6, dt);
    if (!isFlipping() && Math.abs(J.hips.rotation.x) > Math.PI*1.9) {
      J.hips.rotation.x = 0;                       /* normalize after completed flip */
    }

    applyTargets(base, CFG.poseRate, dt, add);

    /* legs: IK when planted or seated; FK tuck during air phase */
    if (footW > 0.02) {
      solveLeg("L", footTargets.L, dt, footW);
      solveLeg("R", footTargets.R, dt, footW);
    } else {
      var tuckLeg = { x: -1.9 }, r2 = 14;
      ["L","R"].forEach(function(s){
        J["hip"+s].rotation.x = dampf(J["hip"+s].rotation.x, -1.55, r2, dt);
        J["knee"+s].rotation.x = dampf(J["knee"+s].rotation.x, 2.2, r2, dt);
        J["ankle"+s].rotation.x = dampf(J["ankle"+s].rotation.x, 0.5, r2, dt);
      });
    }
    /* right-hand handle IK */
    if (ikArm > 0.02) {
      _v.set(0.05, CFG.deck.y+0.075, CFG.handleZ-CFG.deck.z);
      solveArm("R", _v, dt, ikArm);
    }

    /* ---------- face: blink + focus ---------- */
    if (lod === 0) {
      blinkT -= dt;
      if (blinkT <= 0) { blinkPhase = 0.14; blinkT = 2 + Math.random()*3.5; }
      if (blinkPhase > 0) {
        blinkPhase -= dt;
        var bl = blinkPhase > 0.07 ? 1 : blinkPhase/0.07;
        if (meshRefs.lidL){ meshRefs.lidL.scale.y = 0.3+bl*0.9; meshRefs.lidR.scale.y = 0.3+bl*0.9; }
      } else if (meshRefs.lidL) { meshRefs.lidL.scale.y = 0.30; meshRefs.lidR.scale.y = 0.30; }
      J.head.rotation.y = dampf(J.head.rotation.y, Math.sin(noiseT*0.35)*0.10 - bal.lean*0.4, 3, dt);
    }
    /* hair + wind */
    if (meshRefs.hair && lod < 2) {
      meshRefs.hair.rotation.x = dampf(meshRefs.hair.rotation.x, 0.06*wind + Math.sin(noiseT*7)*0.012*wind, 6, dt);
      meshRefs.hair.rotation.z = dampf(meshRefs.hair.rotation.z, Math.sin(noiseT*5.2)*0.015*wind - bal.lean*0.05, 6, dt);
    }
    /* LOD visibility toggles */
    var showSmall = lod < 2;
    if (showSmall !== meshRefs._smallShown) {
      meshRefs._smallShown = showSmall;
      meshRefs.small.forEach(function(o){o.visible=showSmall;});
      meshRefs.face.forEach(function(o){o.visible=showSmall;});
    }

    /* ---------- tie verlet (world space, rendered in spine space) ---------- */
    if (lod < 2) tieStep(dt);

    } /* end visible-only work */

    /* action camera envelope */
    actionCamT = isFlipping() ? dampf(actionCamT, 1, 6, dt) : dampf(actionCamT, 0, 3, dt);
    if (panelDirty || (now - panelLastRefresh) > 250) refreshPanel(now);
  }

  var _tw = V3(), _troot = V3(), _tvel = V3();
  function tieStep(dt) {
    var iters = lod === 0 ? 2 : 1;
    var fdt = 1/60;                              /* fixed-step forces: stable at any frame rate */
    /* root pinned at knot in world space */
    _troot.copy(tie.root); J.spine.localToWorld(_troot);
    /* wind = apparent airflow opposite aircraft velocity */
    var vel = api.getVelocity ? api.getVelocity() : null;
    tie.nodes[0].copy(_troot);
    for (var i=1;i<tie.nodes.length;i++) {
      var p2 = tie.nodes[i], pr = tie.prev[i];
      _tvel.copy(p2).sub(pr).multiplyScalar(0.94);
      if (_tvel.length() > 0.09) _tvel.setLength(0.09);   /* explosion guard */
      pr.copy(p2);
      p2.add(_tvel);
      p2.y -= 9.8*fdt*fdt*1.6;                   /* gravity */
      if (vel) p2.addScaledVector(vel, -fdt*0.02); /* gentle airflow drag, trails aft */
    }
    for (var k=0;k<iters;k++) {
      for (var s=1;s<tie.nodes.length;s++) {
        _tw.copy(tie.nodes[s]).sub(tie.nodes[s-1]);
        var d2 = _tw.length() || 0.0001;
        _tw.multiplyScalar((d2 - tie.segLen)/d2 * (s===1?1:0.5));
        tie.nodes[s].sub(_tw);
        if (s>1) tie.nodes[s-1].add(_tw);
      }
      tie.nodes[0].copy(_troot);
    }
    /* hard sanity: nodes stay near the collar, never NaN — a broken node resets */
    for (var sN=1;sN<tie.nodes.length;sN++) {
      var nd = tie.nodes[sN];
      if (!isFinite(nd.x+nd.y+nd.z) || nd.distanceToSquared(_troot) > 0.3) {
        nd.set(_troot.x, _troot.y - sN*tie.segLen*0.8, _troot.z);
        tie.prev[sN].copy(nd);
      }
    }
    /* write ribbon in spine-local space, keep in front of chest */
    var pos = tie.posAttr.array, w0=0.030, w1=0.052;
    for (var q3=0;q3<tie.nodes.length;q3++) {
      _tw.copy(tie.nodes[q3]); J.spine.worldToLocal(_tw);
      if (_tw.z > -0.13) _tw.z = -0.13;           /* stay off the shirt */
      var w = q3===tie.nodes.length-1 ? 0.012 : lerp(w0,w1,q3/(tie.nodes.length-1));
      pos[q3*6]   = _tw.x - w; pos[q3*6+1] = _tw.y; pos[q3*6+2] = _tw.z;
      pos[q3*6+3] = _tw.x + w; pos[q3*6+4] = _tw.y; pos[q3*6+5] = _tw.z;
    }
    tie.posAttr.needsUpdate = true;
  }

  /* ---------------- action panel UI ---------------- */
  var panelDirty = true, panelLastRefresh = 0, actionCamT = 0;
  var panel = document.createElement("div");
  panel.className = "sfc-actions";
  panel.setAttribute("role","group");
  panel.setAttribute("aria-label","Wing rider actions");
  panel.innerHTML =
    '<div class="sfc-t">WING RIDER</div>' +
    '<button data-act="stand" aria-keyshortcuts="1"><i>1</i>STAND</button>' +
    '<button data-act="sit" aria-keyshortcuts="2"><i>2</i>SIT</button>' +
    '<button data-act="frontflip" aria-keyshortcuts="3"><i>3</i>FRONT FLIP</button>' +
    '<button data-act="backflip" aria-keyshortcuts="4"><i>4</i>BACKFLIP</button>';
  var pcss = document.createElement("style");
  pcss.textContent =
    ".sfc-actions{position:absolute;right:16px;bottom:20px;display:flex;flex-direction:column;gap:5px;z-index:12;pointer-events:auto}" +
    ".sfc-actions .sfc-t{font:600 9px ui-monospace,monospace;letter-spacing:.26em;color:#9a9488;text-align:right;margin-bottom:2px;text-shadow:0 1px 3px rgba(0,0,0,.9)}" +
    ".sfc-actions button{display:flex;align-items:center;gap:8px;background:rgba(5,8,12,.55);border:1px solid rgba(255,255,255,.1);color:#e8e0d0;font:600 10px ui-monospace,monospace;letter-spacing:.18em;padding:8px 12px;border-radius:3px;cursor:pointer;min-width:118px;backdrop-filter:blur(3px);transition:border-color .15s,background .15s,opacity .15s}" +
    ".sfc-actions button i{font-style:normal;color:#c9b99a;border:1px solid rgba(201,185,154,.4);border-radius:2px;padding:0 4px;font-size:9px}" +
    ".sfc-actions button:hover:not([disabled]),.sfc-actions button:focus-visible{border-color:#c9b99a;background:rgba(20,22,18,.75);outline:none}" +
    ".sfc-actions button:active:not([disabled]){transform:translateY(1px)}" +
    ".sfc-actions button[disabled]{opacity:.38;cursor:default}" +
    "@media (max-width:820px){.sfc-actions{right:10px;bottom:14px}.sfc-actions button{min-width:100px;padding:9px 10px}}";
  document.head.appendChild(pcss);
  api.uiRoot.appendChild(panel);
  panel.addEventListener("click", function(ev){
    var b = ev.target.closest ? ev.target.closest("button[data-act]") : null;
    if (b && !b.disabled) action(b.dataset.act);
  });
  function refreshPanel(now) {
    panelDirty = false; panelLastRefresh = now || performance.now();
    var m = { stand: canStand(), sit: canSit(), frontflip: canFlip(), backflip: canFlip() };
    panel.querySelectorAll("button[data-act]").forEach(function(b){
      b.disabled = !m[b.dataset.act];
    });
  }

  /* ---------------- lifecycle ---------------- */
  function onReset() {
    gripping = false; gripT = releaseT = 0;
    setState("standingIdle");
    J.hips.rotation.set(0,0,0);
    J.hips.position.set(0, standHipY, 0);
    footTargets.L.set(-CFG.footSpread, CFG.deck.y+CFG.footH, 0.02);
    footTargets.R.set(CFG.footSpread, CFG.deck.y+CFG.footH, -0.02);
    /* settle the tie instantly at the new pose */
    _troot.copy(tie.root); J.spine.localToWorld(_troot);
    for (var i=0;i<tie.nodes.length;i++){ tie.nodes[i].set(_troot.x,_troot.y-i*tie.segLen,_troot.z); tie.prev[i].copy(tie.nodes[i]); }
  }
  function onCrash() { setState("crashReaction"); gripping = false; }
  function dispose() {
    try {
      if (panel.parentNode) panel.parentNode.removeChild(panel);
      if (pcss.parentNode) pcss.parentNode.removeChild(pcss);
      disposables.forEach(function(g){ try{g.dispose();}catch(e){} });
      for (var k in M) M[k].dispose();
      if (anchor.parent) anchor.parent.remove(anchor);
      if (fixtures.parent) fixtures.parent.remove(fixtures);
    } catch(e) {}
  }
  function getCamFocus(out) { J.chest.getWorldPosition(out); return out; }

  refreshPanel();
  onReset();
  return {
    update: update, action: action, onReset: onReset, onCrash: onCrash,
    dispose: dispose, getCamFocus: getCamFocus,
    actionCamAmount: function(){ return actionCamT; },
    debug: function(){ return { state: st, bal: bal, gripping: gripping, lod: lod, rootPos: [J.hips.position.x, J.hips.position.y, J.hips.position.z], rootRX: J.hips.rotation.x }; }
  };
}
})();
